import { ApiClient } from '../../client';
import {
  MergeSuggestionsResponse,
  MergeSuggestion,
  MergePreview,
  MergeRequest,
  MergeResponse,
  MergeDomainConfig,
  DomainMergeSuggestion,
} from './types';

// Backend API response types
interface BackendSuggestionsResponse {
  suggestions: DomainMergeSuggestion[];
}

interface BackendPreviewResponse {
  status: string;
  source_id: number;
  source_name: string;
  source_user_id: string;
  source_created_date: string;
  target_id: number;
  target_name: string;
  target_user_id: string;
  target_created_date: string;
  overlap_warning: string;
  [key: string]: string | number | boolean | null | undefined;
}

interface BackendMergeResponse {
  success: boolean;
  message: string;
  affected_products?: number;
  affected_facilities?: number;
  affected_count?: number;
}

/**
 * Generic service for merge operations across all domains
 */
export class MergeService {
  constructor(
    private api: ApiClient,
    private config: MergeDomainConfig
  ) {}

  /**
   * Get suggested similar entities for merge
   * Transforms domain-specific field names to generic field names
   */
  async getSuggestions(sourceId: number, maxResults = 10) {
    const queryParams = new URLSearchParams();
    queryParams.append('max_results', maxResults.toString());
    const query = queryParams.toString();

    const response = await this.api.get<BackendSuggestionsResponse>(
      `${this.config.apiBasePath}/${sourceId}/merge/suggestions?${query}`
    );

    // Transform domain-specific suggestions to generic format
    if (!response.data?.suggestions) {
      throw new Error('Invalid response: missing suggestions data');
    }

    const genericSuggestions: MergeSuggestion[] = response.data.suggestions.map(
      s => ({
        id: Number(s[this.config.entityIdField]) || 0,
        name: String(s[this.config.entityNameField]) || '',
        similarity_score: s.similarity_score,
        related_count:
          Number(s[`${this.config.relatedEntityName.slice(0, -1)}_count`]) || 0,
        entity: s, // Preserve original entity data
      })
    );

    return {
      ...response,
      data: {
        suggestions: genericSuggestions,
      } as MergeSuggestionsResponse,
    };
  }

  /**
   * Get merge preview showing impact before execution
   * Transforms domain-specific field names to generic field names
   */
  async getPreview(sourceId: number, targetId: number) {
    const response = await this.api.get<BackendPreviewResponse>(
      `${this.config.apiBasePath}/${sourceId}/merge/preview/${targetId}`
    );

    // Transform domain-specific preview to generic format
    if (!response.data) {
      throw new Error('Invalid response: missing preview data');
    }

    const data = response.data;
    const relatedCountField = `${this.config.relatedEntityName.slice(0, -1)}_count`; // e.g., product_count

    const genericPreview: MergePreview = {
      ...data, // Preserve all fields from API response (including conflict_* and source_*/target_* fields)
      status: data.status,
      source_id: data.source_id,
      source_name: data.source_name,
      source_user_id: data.source_user_id,
      source_created_date: data.source_created_date,
      source_related_count: Number(data[`source_${relatedCountField}`]) || 0,
      target_id: data.target_id,
      target_name: data.target_name,
      target_user_id: data.target_user_id,
      target_created_date: data.target_created_date,
      target_related_count: Number(data[`target_${relatedCountField}`]) || 0,
      overlapping_related_count:
        Number(data[`overlapping_${this.config.relatedEntityName}`]) || 0,
      total_related_after_merge:
        Number(data[`total_${this.config.relatedEntityName}_after_merge`]) || 0,
      overlap_warning: data.overlap_warning,
    };

    return {
      ...response,
      data: genericPreview,
    };
  }

  /**
   * Execute merge operation
   */
  async executeMerge(
    sourceId: number,
    targetId: number,
    request: MergeRequest
  ) {
    const response = await this.api.post<BackendMergeResponse>(
      `${this.config.apiBasePath}/${sourceId}/merge/${targetId}`,
      request
    );

    // Transform domain-specific response to generic format
    if (!response.data) {
      throw new Error('Invalid response: missing merge result data');
    }

    const data = response.data;
    const affectedCountField =
      data.affected_products ??
      data.affected_facilities ??
      data.affected_count ??
      0;

    const genericResponse: MergeResponse = {
      success: data.success,
      affected_count: affectedCountField,
      message: data.message,
    };

    return {
      ...response,
      data: genericResponse,
    };
  }
}
